import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Clock, RefreshCw } from 'lucide-react';

const SESSION_TIMEOUT = 120 * 60 * 1000; // 120 minutos em millisegundos
const WARNING_TIME = 5 * 60 * 1000; // 5 minutos antes de expirar

const SessionManager = () => {
  const { user, logout } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);

  useEffect(() => {
    if (!user) return;

    let warningTimer;
    let expiryTimer;
    let countdownInterval;

    const resetTimers = () => {
      // Limpar timers existentes
      if (warningTimer) clearTimeout(warningTimer);
      if (expiryTimer) clearTimeout(expiryTimer);
      if (countdownInterval) clearInterval(countdownInterval);

      // Timer para mostrar aviso (120min - 5min = 115min)
      warningTimer = setTimeout(() => {
        setShowWarning(true);
        setTimeRemaining(WARNING_TIME);

        // Iniciar contagem regressiva
        countdownInterval = setInterval(() => {
          setTimeRemaining(prev => {
            if (prev <= 1000) {
              clearInterval(countdownInterval);
              return 0;
            }
            return prev - 1000;
          });
        }, 1000);

        toast.warning('Sua sessão está prestes a expirar', {
          description: 'Sua sessão expirará em 5 minutos por inatividade',
          duration: 10000,
        });
      }, SESSION_TIMEOUT - WARNING_TIME);

      // Timer para expirar sessão (120min)
      expiryTimer = setTimeout(() => {
        setShowWarning(false);
        logout();
        toast.error('Sessão expirada por inatividade', {
          description: 'Faça login novamente para continuar',
          duration: 5000,
        });
        window.location.href = '/login';
      }, SESSION_TIMEOUT);
    };

    // Iniciar timers
    resetTimers();

    // Eventos que resetam o timer de inatividade
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      // Apenas resetar se não estiver mostrando o aviso
      if (!showWarning) {
        resetTimers();
      }
    };

    events.forEach(event => {
      document.addEventListener(event, handleActivity);
    });

    // Cleanup
    return () => {
      if (warningTimer) clearTimeout(warningTimer);
      if (expiryTimer) clearTimeout(expiryTimer);
      if (countdownInterval) clearInterval(countdownInterval);
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [user, logout, showWarning]);

  const handleContinueSession = () => {
    setShowWarning(false);
    toast.success('Sessão renovada com sucesso!');
    // O useEffect detectará a mudança e resetará os timers
  };

  const handleLogout = () => {
    setShowWarning(false);
    logout();
    toast.info('Você saiu do sistema');
    window.location.href = '/login';
  };

  const formatTime = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (!user) return null;

  return (
    <AlertDialog open={showWarning} onOpenChange={setShowWarning}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 bg-amber-100 rounded-full">
              <Clock className="w-6 h-6 text-amber-600" />
            </div>
            <AlertDialogTitle className="text-xl">
              Sessão Expirando
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription className="text-base">
            <div className="space-y-3">
              <p>
                Sua sessão está prestes a expirar por inatividade.
              </p>
              <div className="flex items-center justify-center p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="text-center">
                  <p className="text-sm text-amber-700 mb-1">Tempo restante:</p>
                  <p className="text-3xl font-bold text-amber-600">
                    {formatTime(timeRemaining)}
                  </p>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                Clique em "Continuar Conectado" para renovar sua sessão ou "Sair" para encerrar.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex gap-2">
          <AlertDialogCancel onClick={handleLogout} className="flex-1">
            Sair
          </AlertDialogCancel>
          <AlertDialogAction 
            onClick={handleContinueSession}
            className="flex-1 bg-blue-600 hover:bg-blue-700"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Continuar Conectado
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default SessionManager;
